import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eyebrow, Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Paste the real store URLs here at launch — the badges then replace the waitlist form automatically.
const STORE_URLS = { appstore: "", googleplay: "" };
const STORE_BUTTONS = Boolean(STORE_URLS.appstore || STORE_URLS.googleplay);

function AppleIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
    );
}

function PlayIcon() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M3.6 1.8 13.7 12 3.6 22.2c-.36-.2-.6-.6-.6-1.1V2.9c0-.5.24-.9.6-1.1zm11.2 9.1 2.6-2.6 3.3 1.9c.96.55.96 1.95 0 2.5l-3.3 1.9-2.6-2.6v-1.1zm-1.06 1.06 1.06 1.06-9.2 5.3 8.14-6.36zm1.06-3.18L4.6 2.7l8.2 4.72-1.06 1.06z" />
        </svg>
    );
}

export default function Waitlist() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (loading) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/waitlist`, { email, consent: true });
            toast.success(data.message || "You're on the list.", {
                description: "We'll be in touch the moment EDIT arrives.",
            });
            setEmail("");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string" ? detail : "Please enter a valid email address and try again.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="waitlist" className="bg-white py-24 md:py-40" data-testid="waitlist-section">
            <div className="mx-auto max-w-2xl px-6 text-center sm:px-10">
                <Reveal className="flex justify-center">
                    <Eyebrow>Coming soon</Eyebrow>
                </Reveal>
                <Reveal delay={0.08}>
                    <h2 className="mt-7 text-4xl font-extralight leading-[1.06] tracking-[-0.03em] text-slate-950 sm:text-5xl lg:text-6xl">
                        Your next favourite find{" "}
                        <span className="italic font-light text-slate-950">starts here.</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.16}>
                    <p className="mx-auto mt-7 max-w-md text-base font-extralight leading-relaxed text-slate-600 sm:text-lg" data-testid="waitlist-support-text">
                        Be first to experience a whole new way to discover, style and shop.
                    </p>
                </Reveal>

                {STORE_BUTTONS ? (
                    <Reveal delay={0.2}>
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                            {STORE_URLS.appstore && (
                                <a
                                    href={STORE_URLS.appstore}
                                    className="inline-flex items-center gap-2.5 rounded-full bg-slate-950 px-7 py-3.5 text-sm font-light tracking-wide text-white transition-colors duration-300 hover:bg-slate-800"
                                    data-testid="waitlist-appstore-link"
                                >
                                    <AppleIcon /> App Store
                                </a>
                            )}
                            {STORE_URLS.googleplay && (
                                <a
                                    href={STORE_URLS.googleplay}
                                    className="inline-flex items-center gap-2.5 rounded-full border border-slate-200 bg-white px-7 py-3.5 text-sm font-light tracking-wide text-slate-950 transition-colors duration-300 hover:bg-slate-50"
                                    data-testid="waitlist-googleplay-link"
                                >
                                    <PlayIcon /> Google Play
                                </a>
                            )}
                        </div>
                    </Reveal>
                ) : (
                    <Reveal delay={0.2}>
                        <form
                            onSubmit={submit}
                            className="mx-auto mt-10 flex max-w-md items-center gap-2 rounded-full border border-slate-200 bg-white p-2 pl-6 transition-colors duration-300 focus-within:border-slate-900"
                            data-testid="waitlist-form"
                        >
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Your email address"
                                aria-label="Email address"
                                data-testid="waitlist-email-input"
                                className="w-full min-w-0 flex-1 bg-transparent text-base font-extralight text-slate-950 outline-none placeholder:text-slate-300"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="waitlist-submit-button"
                                className="shrink-0 rounded-full bg-slate-950 px-6 py-3 text-sm font-light tracking-wide text-white transition-colors duration-300 hover:bg-slate-800 active:scale-95 disabled:opacity-60 sm:px-7"
                            >
                                {loading ? "Joining…" : "Join the Waitlist"}
                            </button>
                        </form>
                    </Reveal>
                )}

                <Reveal delay={0.28}>
                    <p className="mx-auto mt-6 max-w-md text-xs font-extralight leading-relaxed text-slate-400">
                        By joining the waitlist you agree to receive updates from EDIT about launch news. No spam —
                        unsubscribe at any time. See our{" "}
                        <a href="/privacy" className="underline underline-offset-2 hover:text-slate-950" data-testid="waitlist-privacy-link">
                            Privacy Policy
                        </a>
                        .
                    </p>
                </Reveal>
            </div>
        </section>
    );
}
